import type { ApiErrorResponse } from './types';

/**
 * Best-effort derivation of a human-readable message from any thrown value.
 * Handles `Error` instances, strings, `{ message }` / `{ error: { message } }`
 * shapes and falls back to `JSON.stringify`.
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message || error.name || 'Unknown error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    if ('message' in error) {
      const msg = (error as { message?: unknown }).message;
      if (typeof msg === 'string') {
        return msg;
      }
    }

    if ('error' in error) {
      const apiError = (error as { error?: ApiErrorResponse }).error;
      if (apiError && typeof apiError.message === 'string') {
        return apiError.message;
      }
    }
  }

  try {
    // JSON.stringify can return undefined (e.g. for `undefined` or a function),
    // so guard against a non-string result to keep the string return contract.
    const serialized = JSON.stringify(error);
    return typeof serialized === 'string'
      ? serialized
      : 'Unknown error (no message)';
  } catch {
    return 'Unknown exception (unserializable object)';
  }
};
